/**
 * EquipmentTypesListPage — Equipment Type catalog list view.
 * STORY-1306 AC#2 (Sprint 17 W3).
 *
 * Pattern: TanStack Query useEquipmentTypes hook + filter UI (search + category +
 * isActive). Table list with edit/view actions. "Új típus" CTA → /admin/equipment-types/new.
 */

import { Button, Card, CardContent, Input } from '@/components/ui';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useEquipmentTypes } from '@/hooks/use-equipment-types';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = ['SMALL_ENGINE', 'LAWN_EQUIPMENT', 'POWER_TOOL', 'OTHER'] as const;

export function EquipmentTypesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [activeOnly, setActiveOnly] = useState(true);

  // CR M8 patch: debounce search keystrokes to avoid request flood.
  const debouncedSearch = useDebouncedValue(search, 300);

  const filter = useMemo(
    () => ({
      ...(debouncedSearch.length > 0 ? { search: debouncedSearch } : {}),
      ...(category !== 'all' ? { category } : {}),
      ...(activeOnly ? { isActive: true } : {}),
    }),
    [debouncedSearch, category, activeOnly]
  );

  const { types, isLoading, error } = useEquipmentTypes(filter);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('equipment.types.list.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t('equipment.types.list.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/admin/equipment-types/new')}
          data-testid="equipment-types-new-cta"
        >
          + {t('equipment.types.list.newCta')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Input
              placeholder={t('equipment.types.list.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1"
              data-testid="equipment-types-search"
            />
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
              data-testid="equipment-types-category-filter"
            >
              <option value="all">{t('equipment.types.list.allCategories')}</option>
              {CATEGORIES.map(c => (
                <option key={c} value={c}>
                  {t(`equipment.types.categories.${c}`)}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={e => setActiveOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="equipment-types-active-only"
              />
              {t('equipment.types.list.activeOnly')}
            </label>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="text-center py-8 text-sm text-muted-foreground">
          {t('equipment.types.list.loading')}
        </div>
      )}

      {error && (
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-4 text-sm text-red-700 dark:text-red-300">{error}</CardContent>
        </Card>
      )}

      {!isLoading && !error && types.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t('equipment.types.list.empty.title')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('equipment.types.list.empty.desc')}
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate('/admin/equipment-types/new')}
              data-testid="equipment-types-empty-cta"
            >
              + {t('equipment.types.list.newCta')}
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && types.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="equipment-types-table">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.name')}
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.sku')}
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.category')}
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.brand')}
                  </th>
                  <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.status')}
                  </th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('equipment.types.list.col.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {types.map(type => (
                  <tr
                    key={type.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    data-testid={`equipment-type-row-${type.id}`}
                  >
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">
                      {type.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {type.sku}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {t(`equipment.types.categories.${type.category}`, {
                        defaultValue: type.category,
                      })}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {type.brand ?? '—'} {type.model ?? ''}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={
                          type.isActive
                            ? 'text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                            : 'text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase bg-gray-100 dark:bg-gray-800 text-muted-foreground'
                        }
                      >
                        {type.isActive
                          ? t('equipment.types.list.active')
                          : t('equipment.types.list.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/equipment-types/${type.id}/edit`)}
                        data-testid={`equipment-type-edit-${type.id}`}
                      >
                        {t('equipment.types.list.actionEdit')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
